import { cva, type VariantProps } from 'class-variance-authority';
import { uiMarker, uiMarkerLayout } from './shared';

/**
 * RATE MATRIX
 *
 * The editable pricing grid on the Rates tab: one row per service, one column
 * per vehicle class, a rate in every cell. It is a grid rather than a Table
 * because every cell is an Input the row's height has to hug, and because the
 * header stays put while the body scrolls under it.
 *
 * Four kinds of cell, and the difference between them is the whole recipe:
 *
 *   corner    top-left, names the row column ("Rate Name")
 *   heading   a vehicle class across the top, tinted by class
 *   label     the service name down the left, on the muted rail
 *   value     the rate itself — the cell draws the rule and sets the tone,
 *             the Input inside it (variant="cell") draws the value in it
 *
 * `active` is the column-and-row highlight that follows the selected cell.
 * `adjusted` is a value cell whose rate has been overridden for this
 * account: it reads in the info colour, where a standard rate is muted.
 */
export const rateMatrixRecipe = cva([
  uiMarker,
  uiMarkerLayout,
  'max-h-[70vh] overflow-x-auto overflow-y-auto',
]);

/** Keeps the grid at least as wide as its frame, so the rules reach the edge. */
export const rateMatrixBodyRecipe = cva([
  uiMarker,
  uiMarkerLayout,
  'min-w-full',
]);

export const rateMatrixRowRecipe = cva([uiMarker, uiMarkerLayout, 'grid'], {
  variants: {
    /** The header row: pinned to the top of the scrolling frame. */
    header: {
      true: 'sticky top-0 z-20 mb-1',
      false: '',
    },
  },
  defaultVariants: { header: false },
});

export const rateMatrixCellRecipe = cva([uiMarker, 'border-b border-border'], {
  variants: {
    kind: {
      corner:
        'border-r border-border bg-muted/50 p-2 font-sans text-body-sm font-semibold',
      heading: 'p-2 text-center font-sans text-body-sm font-semibold',
      label: 'group relative border-r border-border bg-muted/50 p-2',
      value: 'group relative text-muted-foreground',
    },
    /** The vehicle class's tint. Only a heading carries one. */
    tone: {
      none: '',
      neutral: 'bg-muted',
      info: 'bg-info-subtle',
      success: 'bg-success-subtle',
      warning: 'bg-warning-subtle',
      processing: 'bg-processing-subtle',
      destructive: 'bg-destructive-subtle',
    },
    /** In the selected cell's row or column. */
    active: {
      true: 'text-info',
      false: '',
    },
    /** A value overridden for this account, against the standard rate. */
    adjusted: {
      true: 'font-semibold text-info',
      false: '',
    },
  },
  defaultVariants: {
    kind: 'value',
    tone: 'none',
    active: false,
    adjusted: false,
  },
});

export type RateMatrixRowRecipeProps = VariantProps<typeof rateMatrixRowRecipe>;
export type RateMatrixCellRecipeProps = VariantProps<
  typeof rateMatrixCellRecipe
>;
